import React, { useState, useMemo, useEffect } from 'react';
import { Card, Tag, Button, Row, Col, Typography, Space, Avatar, Tooltip } from 'antd';
import { 
    TrophyOutlined, 
    ClockCircleOutlined, 
    DollarOutlined,
    PlayCircleOutlined,
    UserOutlined,
    CrownOutlined,
    StarOutlined,
} from '@ant-design/icons';
import { cloneDeep } from 'lodash';
import Team from '../../components/Team';
import { data1, data2 } from './zhuozhuo';
import type { ZhuozhuoEntry, TrackConfig } from '../../types';
import styles from './index.module.css';

const { Title, Text, Paragraph } = Typography;

const MockRankPage: React.FC = () => {
    const [selectedTrack, setSelectedTrack] = useState<string>('全部');
    const [selectedSeason, setSelectedSeason] = useState<number>(2); // 默认第二届

    // 赛道配置
    const trackConfig: Record<string, TrackConfig> = {
        '全部': { color: 'default', icon: <TrophyOutlined /> },
        '0 成本赛道': { color: 'red', icon: <DollarOutlined /> },
        '6 成本赛道': { color: 'orange', icon: <DollarOutlined /> },
        '12 成本赛道': { color: 'gold', icon: <DollarOutlined /> },
        '无限制赛道': { color: 'green', icon: <TrophyOutlined /> },
        '竞速赛道': { color: 'purple', icon: <ClockCircleOutlined /> },
        '低成本赛道': { color: 'cyan', icon: <DollarOutlined /> }
    };

    // 根据选择的赛事获取数据源
    const currentData = useMemo<ZhuozhuoEntry[]>(() => {
        return selectedSeason === 1 ? data1 : data2;
    }, [selectedSeason]);

    // 从当前赛事数据中提取所有唯一的赛道类型
    const availableTracks = useMemo<string[]>(() => {
        const tracks = new Set(currentData.map(item => item.type));
        return ['全部', ...Array.from(tracks).sort()];
    }, [currentData]);

    // 当切换赛事时，如果当前选中的赛道在新赛事中不存在，重置为"全部"
    useEffect(() => {
        if (selectedTrack !== '全部' && !currentData.some(item => item.type === selectedTrack)) {
            setSelectedTrack('全部');
        }
    }, [currentData, selectedTrack]);

    // 过滤和排序数据
    const filteredAndSortedData = useMemo<ZhuozhuoEntry[]>(() => {
        const cloneData = cloneDeep(currentData);

        let filteredData = selectedTrack === '全部' 
            ? cloneData 
            : cloneData.filter(item => item.type === selectedTrack);

        // 排序逻辑：低成本赛道按成本排序，其他按时间排序
        return filteredData.sort((a, b) => {
            if (selectedTrack === '低成本赛道') {
                return a.cost - b.cost;
            }
            return a.time - b.time;
        });
    }, [currentData, selectedTrack]);

    // 获取排名标识
    const getRankIcon = (index: number): React.ReactNode => {
        switch (index) {
            case 0:
                return <CrownOutlined style={{ color: '#FFD700', fontSize: 20 }} />;
            case 1:
                return <TrophyOutlined style={{ color: '#C0C0C0', fontSize: 18 }} />;
            case 2:
                return <StarOutlined style={{ color: '#CD7F32', fontSize: 16 }} />;
            default:
                return null;
                // return <span className={styles.rankNumber}>{index + 1}</span>;
        }
    };

    // 格式化时间显示
    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0 ')}` : `${secs}s`;
    };

    return (
        <div className={styles.rankContainer}>
            {/* 页面标题 */}
            <div className={styles.headerSection}>
                <Title level={2} className={styles.pageTitle}>
                    <TrophyOutlined className={styles.titleIcon} />
                    第{selectedSeason === 1 ? '一' : '二'}届灼灼杯成绩榜
                </Title>
                <Paragraph className={styles.pageDesc}>
                    审核通过后更新参赛选手成绩，点击赛道标签查看不同赛道排名
                </Paragraph>
            </div>

            {/* 赛事选择器 */}
            <Card className={styles.filterCard} style={{ marginBottom: 16 }}>
                <div className={styles.filterSection}>
                    <Text strong className={styles.filterTitle}>选择赛事：</Text>
                    <div className={styles.trackButtons}>
                        <Button
                            type={selectedSeason === 1 ? 'primary' : 'default'}
                            size="large"
                            className={`${styles.trackButton} ${selectedSeason === 1 ? styles.activeTrack : ''}`}
                            onClick={() => setSelectedSeason(1)}
                            icon={<TrophyOutlined />}
                        >
                            第一届
                        </Button>
                        <Button
                            type={selectedSeason === 2 ? 'primary' : 'default'}
                            size="large"
                            className={`${styles.trackButton} ${selectedSeason === 2 ? styles.activeTrack : ''}`}
                            onClick={() => setSelectedSeason(2)}
                            icon={<TrophyOutlined />}
                        >
                            第二届
                        </Button>
                    </div>
                </div>
            </Card>

            {/* 赛道筛选器 */}
            <Card className={styles.filterCard}>
                <div className={styles.filterSection}>
                    <Text strong className={styles.filterTitle}>选择赛道：</Text>
                    <div className={styles.trackButtons}>
                        {availableTracks.map(track => (
                            <Button
                                key={track}
                                type={selectedTrack === track ? 'primary' : 'default'}
                                size="large"
                                className={`${styles.trackButton} ${selectedTrack === track ? styles.activeTrack : ''}`}
                                onClick={() => setSelectedTrack(track)}
                                icon={trackConfig[track]?.icon}
                            >
                                {track}{track === '竞速赛道' || track === '低成本赛道' ? '（幽境危战）' : ''}
                            </Button>
                        ))}
                    </div>
                </div>
            </Card>

            {/* 成绩统计 */}
            <Row gutter={[16, 16]} className={styles.statsRow}>
                <Col xs={24} sm={8}>
                    <Card className={styles.statCard}>
                        <Space>
                            <Avatar size={40} style={{ backgroundColor: '#1890ff' }}>
                                <UserOutlined />
                            </Avatar>
                            <div>
                                <Text className={styles.statNumber}>{filteredAndSortedData.length}</Text>
                                <br />
                                <Text type="secondary">参赛选手</Text>
                            </div>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className={styles.statCard}>
                        <Space>
                            <Avatar size={40} style={{ backgroundColor: '#52c41a' }}>
                                <ClockCircleOutlined />
                            </Avatar>
                            <div>
                                <Text className={styles.statNumber}>
                                    {filteredAndSortedData.length > 0 ? formatTime(Math.min(...filteredAndSortedData.map(item => item.time))) : '-'}
                                </Text>
                                <br />
                                <Text type="secondary">最佳时间</Text>
                            </div>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className={styles.statCard}>
                        <Space>
                            <Avatar size={40} style={{ backgroundColor: '#faad14' }}>
                                <DollarOutlined />
                            </Avatar>
                            <div>
                                <Text className={styles.statNumber}>
                                    {filteredAndSortedData.length > 0 ? Math.min(...filteredAndSortedData.map(item => item.cost)) : '-'}
                                </Text>
                                <br />
                                <Text type="secondary">最低成本</Text>
                            </div>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {/* 成绩列表 */}
            <div className={styles.resultsSection}>
                <Title level={3} className={styles.sectionTitle}>
                    {selectedTrack === '全部' ? '全部成绩' : `${selectedTrack}排名`}
                    <Text type="secondary" className={styles.resultCount}>
                        （共 {filteredAndSortedData.length} 条记录）
                    </Text>
                </Title>

                <Row gutter={[16, 16]}>
                    {filteredAndSortedData.map((result, index) => (
                        <Col xs={24} lg={12} xl={8} key={`${result.uid}-${result.type}-${result.time}`}>
                            <Card 
                                className={`${styles.resultCard} ${index < 3 ? styles.topThree : ''}`}
                                hoverable
                            >
                                {/* 排名和赛道标签 */}
                                <div className={styles.cardHeader}>
                                    <div className={styles.rankSection}>
                                        {getRankIcon(index)}
                                        <Tag 
                                            color={trackConfig[result.type]?.color || 'default'}
                                            className={styles.trackTag}
                                        >
                                            {result.type}
                                        </Tag>
                                    </div>
                                    <Tooltip title="观看视频">
                                        <Button 
                                            type="text" 
                                            icon={<PlayCircleOutlined />}
                                            className={styles.videoButton}
                                            onClick={() => window.open(result.video, '_blank')}
                                        />
                                    </Tooltip>
                                </div>

                                {/* 玩家信息 */}
                                <div className={styles.playerInfo}>
                                    <Avatar 
                                        size={32} 
                                        style={{ backgroundColor: '#722ed1' }}
                                        className={styles.playerAvatar}
                                    >
                                        {result.name.charAt(0)}
                                    </Avatar>
                                    <div className={styles.playerDetails}>
                                        <Text strong className={styles.playerName}>
                                            {result.name}
                                        </Text>
                                        <Text type="secondary" className={styles.playerUid}>
                                            UID: {result.uid}
                                        </Text>
                                    </div>
                                </div>

                                {/* 成绩信息 */}
                                <div className={styles.scoreSection}>
                                    <div className={styles.scoreItem}>
                                        <ClockCircleOutlined className={styles.scoreIcon} />
                                        <div>
                                            <Text strong className={styles.scoreValue}>
                                                {formatTime(result.time)}
                                            </Text>
                                            <br />
                                            <Text type="secondary" className={styles.scoreLabel}>
                                                完成时间
                                            </Text>
                                        </div>
                                    </div>
                                    <div className={styles.scoreItem}>
                                        <DollarOutlined className={styles.scoreIcon} />
                                        <div>
                                            <Text strong className={styles.scoreValue}>
                                                {result.cost}
                                            </Text>
                                            <br />
                                            <Text type="secondary" className={styles.scoreLabel}>
                                                总成本
                                            </Text>
                                        </div>
                                    </div>
                                </div>

                                {/* 队伍配置 */}
                                <div className={styles.teamSection}>
                                    <Text strong className={styles.teamTitle}>队伍配置</Text>
                                    <div className={styles.teamContainer}>
                                        <Team data={result.team} />
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {filteredAndSortedData.length === 0 && (
                    <div className={styles.emptyState}>
                        <TrophyOutlined className={styles.emptyIcon} />
                        <Text type="secondary">暂无 {selectedTrack} 的成绩记录</Text>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MockRankPage;
